// Reine Funktionen — Snapping wird auf Command-Werte angewendet, nie im Renderer.

export function snapToGrid(valueMm, gridMm = 10) {
  return Math.round(valueMm / gridMm) * gridMm
}

export function snapAngle(deg, stepDeg = 90) {
  return ((Math.round(deg / stepDeg) * stepDeg) % 360 + 360) % 360
}

// Pfeiltasten-Nudge in der Bodenebene entlang der Weltachsen:
// ←/→ = pos_x ∓/±, ↑/↓ = pos_z ∓/± (↑ = von der Startkamera weg).
// Liefert Feld + Betrag in mm oder null für Nicht-Pfeiltasten.
export function nudgeDelta(key, stepMm) {
  const map = {
    ArrowLeft: { field: "pos_x", mm: -stepMm },
    ArrowRight: { field: "pos_x", mm: stepMm },
    ArrowUp: { field: "pos_z", mm: -stepMm },
    ArrowDown: { field: "pos_z", mm: stepMm }
  }
  return map[key] ?? null
}

// --- Flächen-Snap (bündiges Andocken beim Drag) -----------------------------

const DEG = Math.PI / 180

// Rotationsmatrix für Euler XYZ in Grad (gleiche Reihenfolge wie three.js-Default)
function rotationMatrix(rxDeg, ryDeg, rzDeg) {
  const [cx, sx] = [Math.cos(rxDeg * DEG), Math.sin(rxDeg * DEG)]
  const [cy, sy] = [Math.cos(ryDeg * DEG), Math.sin(ryDeg * DEG)]
  const [cz, sz] = [Math.cos(rzDeg * DEG), Math.sin(rzDeg * DEG)]
  const rx = [[1, 0, 0], [0, cx, -sx], [0, sx, cx]]
  const ry = [[cy, 0, sy], [0, 1, 0], [-sy, 0, cy]]
  const rz = [[cz, -sz, 0], [sz, cz, 0], [0, 0, 1]]
  const mul = (a, b) => a.map((row, i) => row.map((_, j) => a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j]))
  return mul(mul(rx, ry), rz)
}

// Lokalen Richtungsvektor eines Teils in Weltkoordinaten drehen
export function rotateLocalVector(part, vector) {
  const rotation = rotationMatrix(part.rot_x, part.rot_y, part.rot_z)
  return [ 0, 1, 2 ].map((i) =>
    rotation[i][0] * vector[0] + rotation[i][1] * vector[1] + rotation[i][2] * vector[2]
  )
}

// Welt-AABB eines (rotierten) Quader-Teils. Achsen: 0=x, 1=y, 2=z.
export function partAabb(part) {
  const half = [part.length_mm / 2, part.thickness_mm / 2, part.width_mm / 2]
  const rotation = rotationMatrix(part.rot_x, part.rot_y, part.rot_z)
  const center = [part.pos_x, part.pos_y, part.pos_z]
  const extent = [0, 1, 2].map((i) =>
    Math.abs(rotation[i][0]) * half[0] + Math.abs(rotation[i][1]) * half[1] + Math.abs(rotation[i][2]) * half[2]
  )
  return {
    min: center.map((c, i) => c - extent[i]),
    max: center.map((c, i) => c + extent[i])
  }
}

function overlaps(a, b, axis, margin) {
  return a.min[axis] <= b.max[axis] + margin && b.min[axis] <= a.max[axis] + margin
}

const FACE_AXES = {
  x: [ 0, "pos_x", "snappedX" ],
  y: [ 1, "pos_y", "snappedY" ],
  z: [ 2, "pos_z", "snappedZ" ]
}

// Rastet die Kandidaten-Position bündig an Flächen anderer Teile
// (anliegend max↔min oder flächenbündig min↔min/max↔max).
// axes: welche Achsen snappen (Boden-Drag x/z, Heben y → Stapeln).
export function snapToFaces(part, candidate, otherParts, tolerance = 8, axes = [ "x", "z" ]) {
  const moving = partAabb({ ...part, ...candidate })
  const result = { ...candidate, snappedX: false, snappedY: false, snappedZ: false }

  for (const [axis, posKey, snappedKey] of axes.map((key) => FACE_AXES[key])) {
    const crossAxes = [0, 1, 2].filter((a) => a !== axis)
    let best = null

    for (const other of otherParts) {
      if (other.client_id === part.client_id) continue
      const target = partAabb(other)
      if (!crossAxes.every((a) => overlaps(moving, target, a, tolerance))) continue

      const offsets = [
        target.min[axis] - moving.max[axis], // anliegend davor
        target.max[axis] - moving.min[axis], // anliegend dahinter
        target.min[axis] - moving.min[axis], // flächenbündig min
        target.max[axis] - moving.max[axis]  // flächenbündig max
      ]
      for (const offset of offsets) {
        if (Math.abs(offset) <= tolerance && (best === null || Math.abs(offset) < Math.abs(best))) {
          best = offset
        }
      }
    }

    if (best !== null) {
      result[posKey] = Math.round(candidate[posKey] + best)
      result[snappedKey] = true
    }
  }

  return result
}
